import {useCurrentRepository} from '@github-ui/current-repository'
import {repositoryTreePath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import {ScreenReaderHeading} from '@github-ui/screen-reader-heading'
import {PlusIcon, UploadIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, registerPortalRoot} from '@primer/react'
import {useEffect, useRef} from 'react'
import {useFilesPageInfo} from '../contexts/FilesPageInfoContext'
import {useReposAnalytics} from '../hooks/use-repos-analytics'

import styles from './AddFileDropdownButton.module.css'

const PORTAL_CONTAINER_NAME = 'add-file-dropdown-portal-root'

export function AddFileDropdownButton({useDynamicSizing}: {useDynamicSizing?: boolean}) {
  const {refInfo, path} = useFilesPageInfo()
  const repo = useCurrentRepository()
  const {sendRepoClickEvent} = useReposAnalytics()

  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (containerRef.current) {
      registerPortalRoot(containerRef.current, PORTAL_CONTAINER_NAME)
    }
  }, [])

  if (!refInfo.canEdit) {
    return null
  }

  return (
    <div ref={containerRef}>
      <ScreenReaderHeading as="h2" text="Add file" />
      <ActionMenu>
        <ActionMenu.Anchor>
          <Button aria-label="Add file">
            <span className={useDynamicSizing ? 'react-directory-add-file-button' : undefined}>
              Add file
              <TriangleDownIcon />
            </span>
            {useDynamicSizing && <PlusIcon className="react-directory-add-file-icon" />}
          </Button>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay className={styles.ActionMenu_Overlay} portalContainerName={PORTAL_CONTAINER_NAME}>
          <ActionList>
            <ActionList.LinkItem
              as={Link}
              onClick={() => sendRepoClickEvent('NEW_FILE_BUTTON')}
              to={repositoryTreePath({repo, path, commitish: refInfo.name, action: 'new'})}
            >
              <ActionList.LeadingVisual>
                <PlusIcon />
              </ActionList.LeadingVisual>
              Create new file
            </ActionList.LinkItem>
            <ActionList.LinkItem
              onClick={() => sendRepoClickEvent('UPLOAD_FILES_BUTTON')}
              href={repositoryTreePath({repo, path, commitish: refInfo.name, action: 'upload'})}
            >
              <ActionList.LeadingVisual>
                <UploadIcon />
              </ActionList.LeadingVisual>
              Upload files
            </ActionList.LinkItem>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </div>
  )
}

try{ AddFileDropdownButton.displayName ||= 'AddFileDropdownButton' } catch {}